import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import { Flex, Box } from 'reflexbox';
import { Panel, PanelHeader, Text, Space, Message } from 'rebass';

import { If } from 'frontend/components';

import PawnDisplay from './PawnDisplay';

const statNames = {
  Agi: 'Agility',
  Str: 'Strength',
  Sta: 'Stamina',
  Int: 'Intellect',
  Spi: 'Spirit',

  Crit: 'Critical Strike',
  Haste: 'Haste',
  Mastery: 'Mastery',
  Vers: 'Versatility',

  Armor: 'Armor',
  BonusArmor: 'Bonus Armor',
  Wdps: 'Weapon DPS',
  WOHdps: 'Off Hand Weapon DPS',
  SP: 'Spell Power',
  AP: 'Ap',
};

const StatWeightReport = ({ player, tank, charClass, spec, fightStyle, date }) => {
  let scaleFactors;
  let oldFactors = false;
  if (_.get(player, 'scale_factors_all.dps')) {
    scaleFactors = _.get(player, 'scale_factors_all.dps');
  } else {
    scaleFactors = _.get(player, 'scale_factors');
    oldFactors = true;
  }

  const weights = _.reverse(
    _.sortBy(
      _.filter(
        _.toPairs(
          scaleFactors,
        ),
        factor => factor[1],
      ),
      factor => factor[1],
    ),
  );

  if (_.isEmpty(weights)) {
    return (
      <Message theme="warning">
        SimulationCraft was unable to generate stat weights for your character.
      </Message>
    );
  }

  if (weights[0][0] === 'Wdps') {
    const tmp = weights.shift();
    weights.push(tmp);
  }


  let scaleDelta = 116;
  // eslint-disable-next-line camelcase
  if (player?.scale_deltas) {
    // eslint-disable-next-line camelcase
    scaleDelta = player.scale_deltas[weights[0][0]];
  }

  const maxStatName = statNames[weights[0][0]] || weights[0][0];
  const dpsPerPoint = _.round(weights[0][1], 2).toFixed(2);


  if (oldFactors && tank) {
    return (
      <Message theme="warning">
        Tank DTPS Stat Weights are no longer available as they were always sketchy and not particularly useful.
        Run a new Stat Weights sim to get DPS weights for your tank.
      </Message>
    );
  }

  return (
    <Box mb={2}>
      <Box>
        <Panel theme="primary" mt={1}>
          <PanelHeader p={1}>
            <Text>Stat Weights</Text>
          </PanelHeader>
          {weights.map(weight => (
            <Flex key={weight[0]}>
              <Text>{statNames[weight[0]] || weight[0]}</Text>
              <Space auto />
              <Text>{_.round(weight[1], 2).toFixed(2)}</Text>
            </Flex>
          ))}
          <Text small mt={1}>
            <If
              condition={scaleDelta}
              then={() => (
                <>
                  * If you added {scaleDelta} {maxStatName} to your character, each point would add an average of
                  {' '}
                  {dpsPerPoint} DPS (this does not guarantee that 1 {maxStatName} = {dpsPerPoint} DPS)
                </>
              )}
              else={() => (
                <>
                  * On average, 1 point of {maxStatName} will increase your
                  {' '}
                  DPS by {_.round(weights[0][1], 2).toFixed(2)}
                </>
              )}
            />
          </Text>
        </Panel>
      </Box>

      <Box>
        <PawnDisplay
          charName={player.name}
          charClass={charClass}
          spec={spec}
          fightStyle={fightStyle}
          date={date}
          factors={scaleFactors}
        />
      </Box>
    </Box>
  );
};

StatWeightReport.propTypes = {
  player: PropTypes.shape({
    scale_factors: PropTypes.object,
    name: PropTypes.string,
    specialization: PropTypes.string,
    scale_deltas: PropTypes.object,
  }).isRequired,
  charClass: PropTypes.string,
  spec: PropTypes.string,
  tank: PropTypes.bool,
  date: PropTypes.instanceOf(Date),
  fightStyle: PropTypes.string,
};
StatWeightReport.defaultProps = {
  charClass: '',
  spec: '',
  tank: false,
  fightStyle: '',
  date: null,
};

export default StatWeightReport;
